import { useRef } from 'react';

import { IconButton } from '@/design-system';
import { PHOTO_ACCEPT } from '@/entities/photo';

import { surveyCopy } from '../copy';
import { PhotoNotice } from './PhotoNotice';
import { takePhotoFiles } from './photo-intake';

export interface PhotoButtonsProps {
    /** Hoeveel foto's er al zijn: "Foto (2)". */
    count: number;
    onAddPhotos: (files: File[]) => void;
}

/**
 * "Foto" levert één opname uit de camera, "Uploaden" één of meer foto's uit de
 * bibliotheek - ook HEIC, die de server omzet. Dezelfde knoppen onder een vraag
 * en in het formulier van een eigen bevinding.
 *
 * Twee iconen van 44 px, zonder tekst: zo passen vraag en knoppen op één
 * regel en beginnen de antwoorden hoger. Hun naam ("Foto (2)", "Uploaden")
 * staat in `aria-label`; hoeveel foto's er zijn, zie je aan de foto's zelf.
 *
 * Beide invoervelden lopen via `takePhotoFiles()`: elk bestand wordt apart
 * aangenomen, en wat niet meekan krijgt zijn eigen melding.
 */
export function PhotoButtons({ count, onAddPhotos }: PhotoButtonsProps) {
    const cameraRef = useRef<HTMLInputElement>(null);
    const uploadRef = useRef<HTMLInputElement>(null);

    function pick(input: HTMLInputElement | null) {
        const files = Array.from(input?.files ?? []);

        /*
         * Meteen leegmaken, nog vóór het lezen: anders blijft de vorige keuze
         * staan en geeft dezelfde foto opnieuw kiezen geen `change` meer.
         */
        if (input) {
            input.value = '';
        }

        void takePhotoFiles(files, onAddPhotos);
    }

    return (
        <>
            <IconButton
                variant="ghost"
                size="md"
                icon="takePhoto"
                aria-label={surveyCopy.question.photo(count)}
                className="text-[var(--accent-dark)]"
                onClick={() => cameraRef.current?.click()}
            />
            <IconButton
                variant="ghost"
                size="md"
                icon="upload"
                aria-label={surveyCopy.question.upload}
                className="text-[var(--accent-dark)]"
                onClick={() => uploadRef.current?.click()}
            />

            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                aria-hidden="true"
                tabIndex={-1}
                data-photo-input="camera"
                onChange={() => pick(cameraRef.current)}
            />
            <input
                ref={uploadRef}
                type="file"
                accept={PHOTO_ACCEPT}
                multiple
                hidden
                aria-hidden="true"
                tabIndex={-1}
                data-photo-input="upload"
                onChange={() => pick(uploadRef.current)}
            />

            <PhotoNotice />
        </>
    );
}

/**
 * Wat je ziet terwijl je foto's boven een kaart houdt. Het vlak vangt geen
 * muis- of aanraakgebeurtenissen: de kaart eronder blijft de dropzone.
 */
export function DropOverlay() {
    return (
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[10px] border-2 border-dashed border-[var(--accent)] bg-[var(--accent-tint)]"
        >
            <span className="rounded-[7px] bg-[var(--surface-card)] px-3 py-2 text-[14px] font-medium text-[var(--accent-dark)]">
                {surveyCopy.question.dropHere}
            </span>
        </div>
    );
}
